import shortid from 'shortid';
import { Editor, Transforms, Element } from 'slate';

import { withResetBlockType } from './reset-block-type';
import { unwrapNodesByType } from './transforms';
import { WithListOptions } from './types';

const getVariantForList = (editor: Editor, path: Array<number>) => {
  if (path[0] <= 0) {
    return 'base';
  }

  const previousPath = [path[0] - 1, 0];

  const [previousContent] = Editor.parent(editor, previousPath);
  if (Element.isElement(previousContent) && previousContent.type === 'Typography') {
    return previousContent.data.variation || 'base';
  }

  return 'base';
};

// TODO should these types have defaults since they are optional ?
// this is important when unwrapping, remove defaults to see the problem

export const withList = ({
  typeUl = 'ul',
  typeOl = 'ol',
  typeLi = 'li',
  onDefault = () => ({ type: 'Typography' }),
}: WithListOptions = {}) => <T extends Editor>(editor: T) => {
  const { normalizeNode } = editor;

  /**
   * normalization logic
   */
  // eslint-disable-next-line no-param-reassign
  editor.normalizeNode = (entry) => {
    const [node, path] = entry;

    // if ListItem is not a child of List
    if (Element.isElement(node) && node.type === 'ListItem') {
      const [parent] = Editor.parent(editor, path);

      if (!node.data.variant) {
        Transforms.setNodes(
          editor,
          { data: { variant: getVariantForList(editor, path) } },
          { at: path },
        );
      }

      if (Element.isElement(parent) && parent.type !== 'List') {
        // wrap in list and return
        Transforms.wrapNodes(
          editor,
          {
            type: 'List',
            children: [],
            id: shortid(),
            data: {
              // TODO(mat): we should have a smarter way of infering this defaults
              css: {
                paddingLeft: 40,
                marginBottom: 16,
              },
              variant: 'UL',
            },
          },
          { at: path },
        );
        return;
      }
    }

    if (Element.isElement(node) && node.type === 'List') {
      // TODO(mat): ensure default List variants is added
      if (!node.data?.variant) {
        Transforms.setNodes(
          editor,
          {
            data: {
              ...(node.data || {}),
              variant: 'UL',
            },
          },
          {
            at: path,
          },
        );
        return;
      }

      try {
        const nodeData = Editor.next(editor, { at: path });
        // if two Lists are next to each other, merge them
        if (nodeData && nodeData[0].type === 'List') {
          const nextNodePath = nodeData[1];
          Transforms.mergeNodes(editor, { at: nextNodePath });
          return;
        }
      } catch (e) {
        // slate throws if we try and get paths that don't exist, in this case do nothing
      }
    }

    // Fall back to the original `normalizeNode` to enforce other constraints.
    normalizeNode(entry);
  };

  const onResetListType = () => {
    unwrapNodesByType(editor, typeLi, { split: true });
    unwrapNodesByType(editor, [typeUl, typeOl], { split: true });
  };

  // eslint-disable-next-line no-param-reassign
  editor = withResetBlockType({
    types: [typeLi],
    onDefault,
    onUnwrap: onResetListType,
  })(editor);

  return editor;
};
